import { formatDecimal, isEmpty } from "../utils/tool";
import type { MonitorSdk, MonitorPlugin } from "../types/type";
import { onCLS, onLCP, onINP } from 'web-vitals';

export default class BrowserPerformance implements MonitorPlugin {
  init(sdk: MonitorSdk) {
    if (typeof window === 'undefined') return;

    const onLoad = () => {
      const basic = getBasic();
      const resources = getResources();

      if (!isEmpty(basic)) {
        sdk.report({
          module: 'performance',
          action: 'performance_basic',
          elapsed: basic.duration,
          logs: (basic),
        });
      }

      if (!isEmpty(resources)) {
        sdk.report({
          module: 'performance',
          action: 'resources',
          elapsed: basic.resource,
          logs: (resources),
        });
      }

    }

    onLCP((metric) => {
      // console.log('LCP:', metric);
      sdk.report({
        module: 'performance',
        action: 'lcp',
        elapsed: formatDecimal(metric.value, 3),
        status: metric.rating,
        logs: (metric.entries),
      });
    });

    onCLS((metric) => {
      // console.log('CLS:', metric);
      sdk.report({
        module: 'performance',
        action: 'cls',
        elapsed: formatDecimal(metric.value, 3),
        status: metric.rating,
        logs: (metric.entries),
      });
    });

    onINP((metric) => {
      // console.log('INP:', metric);
      sdk.report({
        module: 'performance',
        action: 'inp',
        elapsed: formatDecimal(metric.value, 3),
        status: metric.rating,
        logs: (metric.entries),
      });
    });

    if (document.readyState === 'complete') {
      setTimeout(onLoad, 0);
    } else {
      window.addEventListener('load', () => {
        setTimeout(onLoad, 0);
      });
    }
  }

}

/**
 * vConsole的实现 https://github.com/Tencent/vConsole/blob/dev/src/log/system.ts
 */

interface BasicType {
  dns: number; // DNS 解析耗时
  tcp: number; // TCP 连接耗时
  ssl: number; // SSL 安全连接耗时
  request: number; // TTFB 收到第一个字节的时间
  response: number; // 数据传输耗时
  domParse: number; // DOM 解析耗时
  resource: number; // 资源加载耗时
  domReady: number; // DOM Ready
  interactive: number; // 首次可交互时间
  complete: number; // 页面完全加载
  redirect: number; // 重定向次数
  redirectTime: number; // 重定向耗时
  duration: number; // 资源请求的总耗时 responseEnd-startTime
  fp: number; // 渲染出第一个像素点，白屏时间
  fcp: number; // 渲染出第一个内容，首屏结束时间
}

function getBasic(): BasicType {
  if (typeof performance === 'undefined') return {} as BasicType;
  const {
    domainLookupEnd,
    domainLookupStart,
    connectEnd,
    connectStart,
    secureConnectionStart,
    loadEventStart,
    // loadEventEnd,
    domInteractive,
    domContentLoadedEventEnd,
    duration,
    responseStart,
    requestStart,
    responseEnd,
    fetchStart,
    // transferSize,
    // encodedBodySize,
    redirectEnd,
    redirectStart,
    redirectCount
  } = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const { startTime: fp } = performance.getEntriesByType('paint').find(({ name }) => name === 'first-paint') || {};
  const { startTime: fcp } = performance.getEntriesByType('paint').find(({ name }) => name === 'first-contentful-paint') || {};
  return {
    dns: formatDecimal(domainLookupEnd - domainLookupStart, 3),
    tcp: formatDecimal(connectEnd - connectStart, 3),
    ssl: formatDecimal(connectEnd - secureConnectionStart, 3),
    request: formatDecimal(responseStart - requestStart, 3),
    response: formatDecimal(responseEnd - responseStart, 3),
    domParse: formatDecimal(domInteractive - responseEnd, 3),
    resource: formatDecimal(loadEventStart - domContentLoadedEventEnd, 3),
    domReady: formatDecimal(domContentLoadedEventEnd - fetchStart, 3),
    interactive: formatDecimal(domInteractive - fetchStart, 3),
    complete: formatDecimal(loadEventStart - fetchStart, 3),
    // httpHead: formatDecimal(encodedBodySize - transferSize, 3),
    redirect: formatDecimal(redirectCount, 3),
    redirectTime: formatDecimal(redirectEnd - redirectStart, 3),
    duration: formatDecimal(duration, 3),
    fp: formatDecimal(Number(fp), 3),
    fcp: formatDecimal(Number(fcp), 3),
  };
}



function getResources() {
  if (typeof performance === 'undefined') return [];
  const resources = performance.getEntriesByType('resource');
  return resources.map((value: PerformanceEntry) => ({
    name: value.name,
    duration: formatDecimal(value.duration, 3)
  }));
}